/* eslint-disable no-console */
import React, { useState, ChangeEvent } from 'react'

import axios from 'axios'
import { useForm, UseFormMethods } from 'react-hook-form'
import * as Masks from 'inter-frontend-lib-util-form/lib/masks'
import * as Validations from 'inter-frontend-lib-util-form/lib/validations'
import * as URLS from 'src/config/api/Urls'
import AcceptTerms from 'src/components/AcceptTerms'
import useDataLayer from 'src/hooks/useDataLayer/dataLayerBody'
import pageContext from 'src/pages/news/pageContext.json'
import { sendCDPFormData } from 'src/shared/cdp'
import ImageWebp from 'src/components/ImageWebp'

import { useLocation } from '@reach/router'

import StatusModal from './_status-modal'
import { Section, BoxContet, SubTitle } from './style'

const imageNews = 'https://central-imagens.bancointer.com.br/85db9e02/log-news.webp'

const acceptTermsText = 'Estou de acordo com a Política de Privacidade do Inter.'

const DATALAYER_SECTION = 'Conteúdos exclusivos para investidores'
interface IFormValues {
  nome: string
  email: string
  cpf: string
}

interface IBrokerFormData {
  campanha: string
  nome: string
  CpfCnpj: string
  email: string
  aceite: boolean
  conteudo01: string
  conteudo02: string
  conteudo03: string
}

const Hero = () => {
  const location = useLocation()
  const { register, errors, handleSubmit, setValue }: UseFormMethods<IFormValues> =
    useForm<IFormValues>()
  const [accept, setAccept] = useState(false)
  const [loading, setLoading] = useState(false)
  const [status, setStatus] = useState('')
  const [sendDatalayerEvent] = useDataLayer()

  const sendForm = async (data: IFormValues) => {
    setLoading(true)

    const urlParams = new URLSearchParams(location.search)

    const utmSource = urlParams.get('utm_source') || undefined
    const utmMedium = urlParams.get('utm_medium') || undefined
    const utmCampaign = urlParams.get('utm_campaign') || undefined
    const hasAnyUTM = utmSource || utmMedium || utmCampaign

    const formData: Partial<IBrokerFormData> = {
      campanha: 'Invest News',
      nome: data.nome,
      CpfCnpj: data.cpf.replace(/\D/g, ''),
      email: data.email,
      aceite: accept === true,
      ...(hasAnyUTM && {
        conteudo01: utmSource,
        conteudo02: utmMedium,
        conteudo03: utmCampaign,
      }),
    }

    try {
      await axios.post(`${URLS.INTER_BROKER_PATH}/lp-mktcloud-inter`, [formData], {
        headers: {
          'Content-Type': 'application/json',
        },
      })

      await Promise.allSettled([
        Promise.resolve(
          sendDatalayerEvent({
            section: DATALAYER_SECTION,
            section_name: DATALAYER_SECTION,
            element_name: 'Assine gratuitamente',
            element_action: 'submit',
            element_previous: '',
            step: 'success',
          }),
        ),
        Promise.resolve(
          sendCDPFormData({
            categoria: pageContext.categoria,
            subCategoria: pageContext.pageTitle,
            tituloPagina: document.title,
            formName: 'formulario newsletter news',
            cpf: formData.CpfCnpj,
            email: data.email,
          }),
        ).catch((error: unknown) => {
          console.error('erro ao enviar dados CDP', error)
        }),
      ])

      setLoading(false)
      setStatus('success')
    } catch (error: unknown) {
      console.error('erro ao enviar formulário', error)

      setLoading(false)
      setStatus('error')
      sendDatalayerEvent({
        section: DATALAYER_SECTION,
        section_name: DATALAYER_SECTION,
        element_name: 'Assine gratuitamente',
        element_action: 'submit',
        element_previous: '',
        step: 'error',
      })
    }
  }

  const handleCloseModal = () => setStatus('')

  return (
    <Section className="d-flex align-items-center" id="hero-modal-form">
      <StatusModal open={status === 'success'} type="success" onClose={handleCloseModal} />
      <StatusModal open={status === 'error'} type="error" onClose={handleCloseModal} />
      <div className="container">
        <div className="row d-flex align-items-center justify-content-between">
          <div className="col-12 col-md-6 d-flex flex-column align-items-center justify-content-center align-items-md-start">
            <ImageWebp
              pathSrc={imageNews}
              altDescription="Conteúdos exclusivos para investidores"
              arrayNumbers={[282.93, 338.04, 375.5, 447.06]}
              arrayNumbersHeight={[98.73, 117.96, 131.03, 156]}
              isHero
            />
            <SubTitle>
              <span className="d-md-block d-xl-inline">
                Receba <strong>conteúdos exclusivos de especialistas</strong>
              </span>{' '}
              para você fazer as melhores decisões com o seu dinheiro.
            </SubTitle>
          </div>
          <div className="col-12 col-md-6 col-lg-5">
            <BoxContet>
              <form onSubmit={handleSubmit(sendForm)}>
                <div className="form-group">
                  <label htmlFor="nome">Nome</label>
                  <input
                    type="text"
                    placeholder="Nome"
                    id="nome"
                    name="nome"
                    ref={register({
                      required: 'Por favor, digite seu nome completo',
                      validate: {
                        isName: (value: string) =>
                          Validations.name(value) || 'Por favor, digite seu nome completo',
                      },
                    })}
                  />
                  {errors.nome && (
                    <p className="fs-12 text-red--base mb-0 text-right">{errors.nome.message}</p>
                  )}
                </div>
                <div className="form-group">
                  <label htmlFor="email">E-mail</label>
                  <input
                    type="text"
                    placeholder="E-mail"
                    id="email"
                    name="email"
                    ref={register({
                      required: 'Digite um e-mail válido',
                      pattern: {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i,
                        message: 'E-mail inválido',
                      },
                    })}
                  />
                  {errors.email && (
                    <p className="fs-12 text-red--base mb-0 text-right">{errors.email.message}</p>
                  )}
                </div>
                <div className="form-group">
                  <label htmlFor="cpf">CPF</label>
                  <input
                    type="text"
                    placeholder="CPF"
                    id="cpf"
                    name="cpf"
                    onChange={(event: ChangeEvent<HTMLInputElement>) =>
                      setValue('cpf', Masks.MaskCPF(event.currentTarget.value))
                    }
                    ref={register({
                      required: 'Digite um CPF válido',
                      validate: {
                        isCpf: (value: string) => Validations.cpf(value) || 'CPF Inválido',
                      },
                    })}
                  />
                  {errors.cpf && (
                    <p className="fs-12 text-red--base mb-0 text-right">{errors.cpf.message}</p>
                  )}
                </div>
                <AcceptTerms accept={accept} setAccept={setAccept} text={acceptTermsText} />
                <button title="Assine gratuitamente" type="submit" disabled={!accept || loading}>
                  {loading ? 'Enviando...' : 'Assine gratuitamente'}
                </button>
              </form>
            </BoxContet>
          </div>
        </div>
      </div>
    </Section>
  )
}

export default Hero
